import React, { FC, useEffect, useState, useRef, useContext } from 'react';
import { useFormikContext } from 'formik';
import { Checkbox } from '@clearcover/atlas';
import {
  GetVehicleByExactVinResponseTypeV2,
  GetVehicleByYearMakeModelBodyV2ResponseType,
  VehicleOptionsResponse,
  VehicleAggregatesV2$GetMakesResponse,
  VehicleAggregatesV2$GetModelsResponse,
  VehicleAggregatesV2$GetBodyStylesResponse,
} from '@clearcover/core-app-types';

import { Row, Column, FormInput } from 'Shared/components';
import { Label } from 'Shared/components/FormInput/Variants';

import { useLazyApi, useLazyPortalV2Api } from 'Shared/services/Api';
import { editVehicleSafetyFeaturesPolicy } from 'Admin/authPolicies';
import { PolicyContext } from 'Policies/utils';
import { AddVehicleFormValues } from '.';
import styles from './styles.module.css';

type VehicleInfoProps = {
  vehicleYears: Record<string, string>;
  data: VehicleOptionsResponse;
};

export const VehicleInfo: FC<VehicleInfoProps> = ({ vehicleYears, data }) => {
  const { resourceEffectiveGrants, details } = useContext(PolicyContext);
  const vinInputRef = useRef<HTMLInputElement>(null);
  const [hasValidVinPrefill, setHasValidVinPrefill] = useState<boolean>(false);
  const {
    handleBlur,
    handleChange,
    setFieldValue,
    values,
    touched,
    setErrors,
    validateForm,
  } = useFormikContext<AddVehicleFormValues>();

  const { askForAntiTheftDevice } = data.vehicleOptionsQuestionsConfig;

  const canEditSafetyFeatures =
    values.type === 'create' ||
    editVehicleSafetyFeaturesPolicy(resourceEffectiveGrants);

  const [
    {
      data: vehicleByVin,
      loading: vehicleByVinLoading,
      error: vehicleByVinError,
    },
    executeVehicleByVin,
  ] = useLazyApi<GetVehicleByExactVinResponseTypeV2>({
    url: 'vehicles/vinv2',
  });

  const [
    { loading: vehicleByInfoLoading, error: vehicleByInfoError },
    executeVehicleByInfo,
  ] = useLazyPortalV2Api<GetVehicleByYearMakeModelBodyV2ResponseType>({
    url: 'vehicles/info',
  });

  const [
    {
      data: vehicleMakesData,
      loading: vehicleMakesLoading,
      error: vehicleMakesError,
    },
    executeMakes,
  ] = useLazyPortalV2Api<VehicleAggregatesV2$GetMakesResponse>({
    url: 'vehicles/makes',
  });
  const [
    {
      data: vehicleModelsData,
      loading: vehicleModelsLoading,
      error: vehicleModelsError,
    },
    executeModels,
  ] = useLazyPortalV2Api<VehicleAggregatesV2$GetModelsResponse>({
    url: 'vehicles/models',
  });
  const [
    {
      data: vehicleBodyStylesData,
      loading: vehicleBodyStylesLoading,
      error: vehicleBodyStylesError,
    },
    executeBodyStyles,
  ] = useLazyPortalV2Api<VehicleAggregatesV2$GetBodyStylesResponse>({
    url: 'vehicles/bodyStyles',
  });

  const vehicleMakes = React.useMemo(() => {
    if (!vehicleMakesData?.makes) return undefined;
    return vehicleMakesData.makes.reduce(
      (acc: Record<string, string>, make: string) => ({
        ...acc,
        [make]: make,
      }),
      {} as Record<string, string>
    );
  }, [vehicleMakesData]);

  const vehicleModels = React.useMemo(() => {
    if (!vehicleModelsData?.models) return undefined;
    return vehicleModelsData.models.reduce(
      (acc: Record<string, string>, model: string) => ({
        ...acc,
        [model]: model,
      }),
      {} as Record<string, string>
    );
  }, [vehicleModelsData]);

  const vehicleBodyStyles = React.useMemo(() => {
    if (!vehicleBodyStylesData?.bodyStyles) return undefined;
    return vehicleBodyStylesData.bodyStyles.reduce(
      (acc: Record<string, string>, bodyStyle: string) => ({
        ...acc,
        [bodyStyle]: bodyStyle,
      }),
      {} as Record<string, string>
    );
  }, [vehicleBodyStylesData]);

  useEffect(() => {
    if (values.vin?.length === 17) {
      vinInputRef?.current?.blur();
    }
  }, [values.vin]);

  const [validatingVin, setValidatingVin] = useState<boolean>(false);

  useEffect(() => {
    if (vehicleByVin?.vehicles?.length) {
      const {
        year: yearByVin,
        make: makeByVin,
        model: modelByVin,
        bodyStyleDescription: bodyStyleDescriptionByVin,
      } = vehicleByVin.vehicles[0];
      setErrors({});
      setFieldValue('year', yearByVin.toString());
      setFieldValue('make', makeByVin);
      setFieldValue('model', modelByVin);
      setFieldValue('body', bodyStyleDescriptionByVin);
      if (bodyStyleDescriptionByVin) {
        executeVehicleByInfo({
          params: {
            year: yearByVin.toString(),
            make: makeByVin,
            model: modelByVin,
            bodyStyle: bodyStyleDescriptionByVin,
            configDiscoveryParams: details.configDiscoveryParams,
          },
        });
      }
      setHasValidVinPrefill(true);
      setValidatingVin(false);
    }
  }, [
    details.configDiscoveryParams,
    vehicleByVin,
    setFieldValue,
    setErrors,
    validateForm,
    executeVehicleByInfo,
  ]);

  useEffect(() => {
    if (!validatingVin) {
      validateForm();
    }
  }, [validatingVin, validateForm]);

  useEffect(() => {
    if (values.type !== 'update') setFieldValue('body', '');
  }, [vehicleBodyStyles, setFieldValue, values.type]);

  const bodyStyleOptions = vehicleByVin
    ? vehicleByVin.vehicles.reduce((all, vehicle) => {
        const bodyStyleCode =
          (vehicle as any).bodyStyleIsoCode || vehicle.bodyStyleDescription;
        return {
          ...all,
          [bodyStyleCode]: vehicle.bodyStyleDescription,
        };
      }, {})
    : vehicleBodyStyles || {};
  const vehicleModelOptions = vehicleByVin
    ? vehicleByVin.vehicles.reduce(
        (all, { model }) => ({
          ...all,
          [model]: model,
        }),
        {}
      )
    : vehicleModels || {};
  return (
    <div className={styles.section} data-testid="vehicle-modal-info">
      <h1>Vehicle Info</h1>
      <Row>
        <Column className={styles.vehicleInfoCol} variant="half">
          <FormInput
            variant="text"
            name="vin"
            data-testid="vin"
            onChange={handleChange}
            onBlur={event => {
              handleBlur(event);
              if (event.target.value) {
                setValidatingVin(true);
                executeVehicleByVin({
                  params: {
                    vin: event.target.value,
                    configDiscoveryParams: details.configDiscoveryParams,
                  },
                }).catch(() => {
                  setHasValidVinPrefill(false);
                  setValidatingVin(false);
                });
              }
            }}
            inputRef={vinInputRef as React.RefObject<HTMLInputElement>}
            value={values.vin}
            errorMessage={touched.vin ? vehicleByVinError?.message : undefined}
            label="VIN"
            disabled={values.type === 'update'}
          />
        </Column>
      </Row>
      <Row>
        <Column className={styles.vehicleInfoCol}>
          <FormInput
            variant="selectAutocomplete"
            name="year"
            data-testid="year"
            autoComplete={'off'}
            onChange={(value: string) => {
              setFieldValue('year', value);
              if (vehicleYears[value] && !hasValidVinPrefill)
                executeMakes({ params: { year: value } });
            }}
            onBlur={handleBlur}
            value={values.year}
            label="Year"
            loading={vehicleByVinLoading}
            disabled={
              !vehicleYears || hasValidVinPrefill || values.type === 'update'
            }
            options={vehicleYears ?? {}}
          />
        </Column>
        <Column className={styles.vehicleInfoCol}>
          <FormInput
            variant="selectAutocomplete"
            name="make"
            data-testid="make"
            autoComplete={'off'}
            onChange={(value: string) => {
              setFieldValue('make', value);
              setFieldValue('model', '');
              setFieldValue('body', '');
              if (vehicleMakes && vehicleMakes[value] && !hasValidVinPrefill)
                executeModels({
                  params: { year: values.year, make: value },
                });
            }}
            onBlur={handleBlur}
            value={values.make}
            errorMessage={touched.make ? vehicleMakesError?.message : undefined}
            label="Make"
            options={vehicleMakes ?? {}}
            loading={
              vehicleMakesLoading || vehicleByVinLoading || vehicleByInfoLoading
            }
            disabled={
              !vehicleYears[values.year] ||
              hasValidVinPrefill ||
              values.type === 'update'
            }
          />
        </Column>
      </Row>
      <Row>
        <Column className={styles.vehicleInfoCol}>
          <FormInput
            variant="selectAutocomplete"
            name="model"
            data-testid="model"
            onChange={(value: string) => {
              setFieldValue('model', value);
              if (
                vehicleModels &&
                vehicleModels[value] &&
                !vehicleByVin?.hasMultipleModels
              ) {
                executeBodyStyles({
                  params: {
                    year: values.year,
                    make: values.make,
                    model: value,
                  },
                });
              }
            }}
            onBlur={handleBlur}
            value={values.model}
            errorMessage={
              touched.model ? vehicleModelsError?.message : undefined
            }
            label="Model"
            autoComplete={'off'}
            options={vehicleModelOptions}
            loading={vehicleModelsLoading || vehicleByVinLoading}
            disabled={
              Object.entries(vehicleModelOptions).length < 1 ||
              (!vehicleByVin?.hasMultipleModels && hasValidVinPrefill) ||
              values.type === 'update'
            }
          />
        </Column>
        <Column className={styles.vehicleInfoCol}>
          <FormInput
            variant="selectAutocomplete"
            name="body"
            data-testid="body"
            autoComplete={'off'}
            onChange={(value: string) => {
              setFieldValue('body', value);
              executeVehicleByInfo({
                params: {
                  year: values.year,
                  make: values.make,
                  model: values.model,
                  bodyStyle: value,
                  configDiscoveryParams: details.configDiscoveryParams,
                },
              });
            }}
            onBlur={handleBlur}
            value={values.body}
            errorMessage={
              touched.body
                ? vehicleBodyStylesError?.message || vehicleByInfoError?.message
                : undefined
            }
            label="Body Style"
            options={bodyStyleOptions}
            loading={vehicleBodyStylesLoading || vehicleByVinLoading}
            disabled={
              Object.entries(vehicleModelOptions).length < 1 ||
              (!vehicleByVin?.hasMultipleBodyStyles && hasValidVinPrefill) ||
              values.type === 'update'
            }
          />
        </Column>
      </Row>
      {!askForAntiTheftDevice && (
        <>
          <Row>
            <Column>
              <Label disabled={!canEditSafetyFeatures}>
                Anti-Theft Features
              </Label>
            </Column>
          </Row>
          <Row>
            <Column style={{ flex: '0 0 33%' }}>
              <Checkbox
                name="alarmsystem"
                value={values.alarmsystem}
                label="Alarm System"
                onChange={() =>
                  canEditSafetyFeatures &&
                  setFieldValue('alarmsystem', !values.alarmsystem)
                }
                className={styles.checkbox}
                disabled={!canEditSafetyFeatures}
              />
              <Checkbox
                name="passivedisable"
                value={values.passivedisable}
                label="Passive Disable"
                onChange={() =>
                  canEditSafetyFeatures &&
                  setFieldValue('passivedisable', !values.passivedisable)
                }
                className={styles.checkbox}
                disabled={!canEditSafetyFeatures}
              />
            </Column>
            <Column style={{ flex: '0 0 33%' }}>
              <Checkbox
                name="activedisable"
                value={values.activedisable}
                label="Active Disable"
                onChange={() =>
                  canEditSafetyFeatures &&
                  setFieldValue('activedisable', !values.activedisable)
                }
                className={styles.checkbox}
                disabled={!canEditSafetyFeatures}
              />
              <Checkbox
                name="vehiclerecovery"
                value={values.vehiclerecovery}
                label="Vehicle Recovery"
                onChange={() =>
                  canEditSafetyFeatures &&
                  setFieldValue('vehiclerecovery', !values.vehiclerecovery)
                }
                className={styles.checkbox}
                disabled={!canEditSafetyFeatures}
              />
            </Column>
          </Row>
        </>
      )}

      <Row></Row>
    </div>
  );
};
